import { ArrowRightIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import type { DashboardSummaryResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The at-a-glance figures — `dashboard-01`'s `SectionCards`, carrying SteelBill's
 * numbers instead of the block's placeholders.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE CARDS, NOT THE BLOCK'S FOUR, AND THAT IS NOT A LAYOUT PREFERENCE.
 * `GET /dashboard/summary` returns exactly three figures: what was billed this
 * month, how many quotations are waiting for a reply, and the last bill. The
 * `financialYear` object carries only labels (`from`, `to`, `label`, `fyShort`,
 * `startYear`) — no count and no total.
 *
 * A fourth card would therefore have to be invented, and the only honest ways to
 * fill it are to add the figure to the API or to leave it out. It is left out.
 * If a year-to-date total is wanted, that is a change to `dashboard.ts`, not a
 * number this component can derive — §6 makes the server the only authority on
 * totals, and a home screen that summed its own paise would be a second tax
 * engine waiting to disagree with the first.
 *
 * EVERY FIGURE IS A LINK, because §11.2 says "a number with no destination is
 * decoration". Tapping the month opens that month's bills; the waiting count
 * opens the SENT quotations; the last bill opens the bill.
 *
 * EVERY AMOUNT GOES THROUGH `formatRupees`, never `toLocaleString` and never the
 * bare `formatPaise`. Indian digit grouping is ₹1,45,600.00 rather than
 * ₹145,600.00, and a runtime built with small-icu falls back to western grouping
 * silently — no error, on a printed invoice.
 *
 * `formatRupees` is `formatPaise` with `symbol: true`. `formatPaise` defaults to
 * `symbol: false`, which is right inside a table column headed "Amount" and
 * wrong on a card whose whole content is one figure — this screen shipped once
 * reading "12,77,645.00" with no ₹ at all.
 *
 * NOTHING HERE IMPLIES PAYMENT STATUS. §11.2 is explicit that the app cannot
 * know who has paid until §17.5 exists, and that even a zero would be a claim.
 * "Billed this month" says billed, and the field behind it is named the same.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function SectionCards({
  summary,
  loading,
}: {
  summary: DashboardSummaryResponse | undefined
  loading: boolean
}) {
  const { t } = useTranslation()

  const grid =
    "*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-3"

  if (loading || summary === undefined) {
    return (
      <div className={grid}>
        {[0, 1, 2].map((index) => (
          <Card key={index} className="@container/card">
            <CardHeader>
              <CardDescription>
                <Skeleton className="h-4 w-28" />
              </CardDescription>
              <CardTitle>
                <Skeleton className="mt-1 h-8 w-32" />
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  const { month, quotationsAwaitingReply: waiting, lastBill } = summary

  return (
    <div className={grid}>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("home.card.billedThisMonth")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {formatRupees(month.totalBilledPaise)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">{t("home.card.bills", { count: month.invoiceCount })}</Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link
            to={routes.invoicesInPeriod(month.from, month.to)}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("home.card.openThisMonth")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("home.card.billedHelp")}</div>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("home.card.waiting")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {waiting.count}
          </CardTitle>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link
            to={routes.quotationsByStatus(waiting.status)}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("home.card.openWaiting")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("home.card.waitingHelp")}</div>
        </CardFooter>
      </Card>

      {/*
        * The last bill. `null` before the first invoice exists — §11.2 wants a
        * document that is not there to be ABSENT rather than zero, because a
        * zero on a brand-new install reads as a broken counter rather than as an
        * empty book.
        */}
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("home.card.lastBill")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {lastBill === null ? "—" : formatRupees(lastBill.amountPaise)}
          </CardTitle>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {lastBill === null ? (
            <div className="text-muted-foreground">{t("home.noBillsYet")}</div>
          ) : (
            <>
              <Link
                to={routes.invoice(lastBill.id)}
                className="line-clamp-1 flex gap-2 font-medium hover:underline"
              >
                {lastBill.number} <ArrowRightIcon className="size-4" />
              </Link>
              <div className="line-clamp-1 text-muted-foreground">
                {lastBill.customerName ?? t("lbl.customerUnknown")}
              </div>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
